/**
 * ████████╗ ██████╗ ██╗     ██╗███╗   ██╗ ██████╗
 * ╚══██╔══╝██╔═══██╗██║     ██║████╗  ██║██╔═══██╗
 *    ██║   ██║   ██║██║     ██║██╔██╗ ██║██║   ██║
 *    ██║   ██║   ██║██║     ██║██║╚██╗██║██║   ██║
 *    ██║   ╚██████╔╝███████╗██║██║ ╚████║╚██████╔╝
 *    ╚═╝    ╚═════╝ ╚══════╝╚═╝╚═╝  ╚═══╝ ╚═════╝
 *
 * (c) Copyright 2021-present Rakuten Kobo Inc. (https://www.kobo.com)
 *
 * Utility functions around everything related to the browser's DOM
 */

// -----------------------------------------------------------------------------
// IMPORTS
// -----------------------------------------------------------------------------

// Internal dependencies
import { getService } from '@services/locator/locator';
import {
  findFirstTextNode,
  isDocumentNode,
  isElementNode,
  isTextNode
} from './node';


// -----------------------------------------------------------------------------
// CONFIGURATION
// -----------------------------------------------------------------------------

/**
 * Map of lowercase node names to identify HTML elements that are displayed
 * as inline by default
 *
 * @type {object}
 */
const IS_INLINE_ELEMENT = {
  "b"      : true,
  "big"    : true,
  "i"      : true,
  "small"  : true,
  "tt"     : true,
  "abbr"   : true,
  "acronym": true,
  "cite"   : true,
  "code"   : true,
  "dfn"    : true,
  "em"     : true,
  "kbd"    : true,
  "strong" : true,
  "samp"   : true,
  "var"    : true,
  "a"      : true,
  "bdo"    : true,
  "br"     : true,
  "q"      : true,
  "script" : true,
  "span"   : true,
  "sub"    : true,
  "sup"    : true
};

const elemPrototype = self.Element?.prototype || { };
const fnMatchesSelector = elemPrototype.matches ||
  elemPrototype.webkitMatchesSelector ||
  elemPrototype.mozMatchesSelector ||
  elemPrototype.msMatchesSelector;


// -----------------------------------------------------------------------------
// PUBLIC API
// -----------------------------------------------------------------------------

/**
 * Checks if DOM @node contains a parser error an logs a warning message to the console
 *
 * @param {HTMLElement} node DOM node
 * @param {String} fileName Name of the parsed file
 * @return {Boolean} True if @node contains a parser error
 */
function checkParserError(node, fileName) {
  if (node && (node.querySelector("parsererror") || node.childNodes.length === 0)) {
    var errorNode = node.querySelector("parsererror > div");
    var message = "Parser Error " + (fileName ? "in file \"" + fileName + "\" -" : "-");

    console.warn(message, errorNode && errorNode.textContent, node);

    return {
      "message": message + errorNode && errorNode.textContent,
      "reason": "PARSER_ERROR"
    };
  }
  return false;
}


/**
 * Creates a DOM element and applies all provided attributes to it
 *
 * @param {string} tagName The name of the HTML tag to create
 * @param {object} attributes HTML attributes for the new element
 * @return {HTMLElement} The newly created element
 */
function createDOMElement(tagName, attributes) {
  var element = document.createElement(tagName);

  for (var name in attributes) {
    if (name === "value" || name === "textContent") {
      element[name] = attributes[name];
    }
    else {
      element.setAttribute(name, attributes[name]);
    }
  }
  return element;
}


/**
 * Returns the range object for the given DOM @node starting from
 * position @startOffset to position @endOffset
 *
 * @param {Node} node HTML Element or text node
 * @param {Integer} [startOffset=0] Start offset given by the number of characters
 * @param {Integer} [endOffset=node.length-1] End offset given by the number of characters
 * @return {Range} Range object for @node
 */
function getRange(node, startOffset, endOffset) {
  if (!node || !node.parentNode) {
    return null;
  }
  var range = node.ownerDocument.createRange();

  if (startOffset !== undefined && isTextNode(node) && node.length > 1) {
    endOffset = endOffset || node.length - 1;
    if (startOffset >= endOffset) {
      startOffset = endOffset - 1;
    }
    range.setStart(node, startOffset);
    range.setEnd(node, endOffset);
  }
  else {
    range.selectNode(node);
  }
  return range;
}


/**
 * Returns the range object for a @node's document selection
 *
 * @param {DOM Node} [node] DOM node
 * @return {Range} the selected range or null if there's no selection
 */
function getSelectedRange(node) {
  const contentDoc = node ? (node.nodeName === "#document" && node || node.contentDocument || node.ownerDocument) : null;
  const selection = contentDoc?.getSelection && contentDoc.getSelection()
                 || document.getSelection();
  const range = selection?.rangeCount > 0 && selection.getRangeAt(0);
  return range && !range.collapsed ? range : null;
}


/**
 * Returns the bounding box (rectangle object with absolute screen positions)
 * for the given text @node
 *
 * @param {TextNode} node Text element node
 * @param {Integer} [startOffset=0] Start offset given by the number of characters
 * @param {Integer} [endOffset=node.length-1] End offset given by the number of characters
 * @return {Object} Rectangle Object with attributes top, left, width, height, bottom, right
 */
function getTextBoundingBox(node, startOffset, endOffset) {
  var range;
  var rect = null;

  if (isTextNode(node)
      && (range = getRange(node, startOffset, endOffset))
      && (rect  = range.getBoundingClientRect())) {
    var clientRect = range.getClientRects()[0];
    // add lineHeight of the 1st client rectangle as an extra property:
    rect.lineHeight = clientRect ? clientRect.height : 0;
  }
  return rect;
}


/**
 * Returns the bounding box (client rectangle object with absolute screen
 * positions) for the given HTML @node.
 *
 * @param {Node} node HTML element or text node
 * @param {Number} [startOffset] Optional start offset given by the number of characters if @node is a text node
 * @return {Object} Rectangle Object with properties top, left, width, height, bottom, right
 */
function getBoundingBox(node, startOffset) {
  return node?.getBoundingClientRect?.()
    || getTextBoundingBox(node, startOffset);
}


/**
 * Returns the style value of the first rule with a style @propertyName defined
 *
 * @param {String} propertyName CSS property name, e.g. "width"
 * @param {CSSRuleList} cssRules List of CSS rules
 * @return {String} Value of the first rule with style @propertyName defined
 */
function getCascadedStyle(propertyName, cssRules) {
  const rule = Array.prototype.find.call(cssRules,
    (rule) => rule.style[propertyName]
  );
  return rule ? rule.style[propertyName] : null;
}


/**
 * Returns the computed style of DOM @node
 *
 * @param {HTMLElement} node DOM element node
 * @return {CSSStyleDeclaration} Computed style of @node
 */
function getComputedStyle(node) {
  return isElementNode(node)
    ? node.ownerDocument.defaultView.getComputedStyle(node, null)
    : null;
}


/**
 * Returns the computed style value of @node's numeric property @propName.
 * For @propName, both names styles (dash or camel-case) are allowed.
 *
 * @param {HTMLElement} node HTML element node
 * @param {String} propName CSS property name, e.g. "font-size" or "fontSize"
 * @return {Number} Property value
 */
function getComputedStyleValue(node, propName) {
  if (!node || !node.ownerDocument) {
    return 0;
  }
  if (isTextNode(node)) {
    return propName === "lineHeight" || propName === "line-height" ? calcLineHeight(node) : 0;
  }
  if (isDocumentNode()) {
    node = node.body;
  }

  var style = node.ownerDocument.defaultView.getComputedStyle(node, null);
  var propValue = style && (style[propName] || style.getPropertyValue(propName));
  // get the numerical part (w/o unit suffix) if the property value is numeric
  var numValue = parseFloat(propValue, 10);

  // special case for property "line-height":
  if (propName === "lineHeight" || propName === "line-height") {
    // for value "normal" the numeric equivalent has to be calculated, otherwise return only positive values:
    return (propValue === "normal") ? calcLineHeight(node) : Math.max(numValue, 0);
  }
  return !isNaN(numValue) ? numValue : propValue || 0;
}


/**
 * Returns a list of all CSS rules from the document's style sheets that apply
 * to @element, @see https://gist.github.com/ydaniv/3033012
 *
 * @param {Element} element DOM element
 * @return {Array} List of CSS rules matching @element
 */
function getMatchedCSSRules(element) {
  const CSS_PAGE_RULE = 6; // = CSSRule.PAGE_RULE
  const result = [];
  const contentDoc = element.ownerDocument || element;
  const matchesSelector = fnMatchesSelector ||
    function(selector) {
      return [].indexOf.call(contentDoc.querySelectorAll(selector), this) >= 0;
    };

  /**
   * Returns the CSS rules in @styleSheet matching current media
   *
   * @param {CSSStyleSheet} styleSheet CSS style sheet
   * @return {Array} List of CSS rules in @styleSheet matching current media
   */
  function getRules(styleSheet) {
    if (styleSheet && !styleSheet.disabled) {
      // Firefox may throw an InvalidAccessError when the styleSheet is not being parsed yet
      try {
        var mediaText = styleSheet.media?.mediaText;
        if (!mediaText || contentDoc.defaultView.matchMedia(mediaText).matches) {
          return Array.prototype.slice.call(styleSheet.cssRules);
        }
      }
      catch { null }
    }
    return [];
  }

  Array.prototype.forEach.call(contentDoc.styleSheets, function(styleSheet) {
    let cssRules = getRules(styleSheet);
    let cssRule;

    while ((cssRule = cssRules.shift())) {
      if (cssRule.styleSheet) {
        // @import rule -> insert the rules at the beginning:
        cssRules = getRules(cssRule.styleSheet).concat(cssRules);
      }
      else if (cssRule.media) {
        // insert the contained rules of this media rule to the beginning of this stylesheet's rules
        cssRules = getRules(cssRule).concat(cssRules);
      }
      else if (cssRule.type !== CSS_PAGE_RULE && cssRule.selectorText) {
        try {
          if (matchesSelector.call(element, cssRule.selectorText)) {
            // element matches this rule's selector -> push the rule to the results set
            result.push(cssRule);
          }
        }
        catch (ex) {
          console.warn(ex);
        }
      }
    }
  });
  // @ToDo: order list by CSS selector specifity
  // @see https://developer.mozilla.org/en-US/docs/Web/CSS/Specificity
  // and @see https://github.com/keeganstreet/specificity/blob/master/specificity.js
  return result;
}


/**
 * Calculates the effective numeric equivalent for the line height of a node.
 * Style declaration "line-height: normal" varies between browsers, font faces,
 * and by font-size.
 *
 * @see http://www.w3.org/TR/CSS21/visudet.html#propdef-line-height
 * @see http://meyerweb.com/eric/thoughts/2008/05/06/line-height-abnormal/
 *
 * @param {HTMLElement} node HTML element node
 * @return {Number} Numeric equivalent for line-height [px]
 */
function calcLineHeight(node) {
  var lineHeight;
  var textNode = findFirstTextNode(node);

  if (textNode) {
    // get the bounding box for the 1st character of textNode:
    var range = getRange(textNode, 0, Math.min(2, textNode.length));
    if (range) {
      var rect = range.getBoundingClientRect();
      lineHeight = rect.height;
    }
  }
  // use as fallback a value based on font-size:
  return lineHeight || getComputedStyleValue(node, "font-size") * 1.2 || 0;
}


/**
 * Indicates if @window supports the SVG namespace
 *
 * @param {Window} window Context window
 * @return {Boolean} Returns true if @window supports the SVG namespace
 */
function hasSVGSupport(window) {
  var createElementNS = window && window.document && window.document.createElementNS;

  try {
    if (createElementNS && createElementNS("http://www.w3.org/2000/svg", "svg").createSVGRect) {
      return true;
    }

  } catch { null }

  const platformService = getService('platform');
  const isAndroid = platformService.isAndroid();
  const osVersion = platformService.getOSVersion();

  return isAndroid || osVersion >= 4.4;
}


/**
 * Indicates if @element is by default an HTML inline level element.
 *
 * @param {HTMLElement} element HTML element
 * @return {Boolean} Returns true if @element is an HTML inline element.
 */
function isInlineElement(element) {
  return IS_INLINE_ELEMENT[element.nodeName.toLowerCase()];
}


/**
 * Returns true if @token is non-empty and a valid class name, otherwise false.
 *
 * @param {String} token CSS class name token
 * @return {Boolean} True if @token is a valid class name
 */
function isValidClassToken(token) {
  return token && /^-?[_a-zA-Z]+[_a-zA-Z0-9-]*$/.test(token);
}


/**
 * Calculates the DOM @element's offset position with respect to @propertyName
 * (`left` or `top`).
 *
 * @param {String} propertyName Offset property name, `left` or `top`
 * @param {Element} element DOM element
 * @return {Number} Accumulated offset value for @element's offset @propertyName
 */
function calcOffset(propertyName, node) {
  const clientRect = getRange(node)?.getBoundingClientRect() || getBoundingBox(node);
  if (!clientRect) {
    console.warn(`calcOffset: no client rectangle for node "${node?.nodeName} ${node?.id || node?.className || ''}"`);
    return 0;
  }
  return clientRect[propertyName];
}


/**
 * Returns the first descendant of @node matching CSS @selectorText, or null if
 * there's no match.
 *
 * @param {Element} node XML element node
 * @param {String} selectorText CSS selector text
 * @return {Node} First DOM node of @node matching @selectorText, or null if there's no match
 */
function querySelector(node, selectorText) {
  try {
    const RE_ATTRIB = /\[[^=\]]+/g;
    // escape colons in attribute names of @selectorText to avoid "not a valid selector"
    // exceptions. A colon may be part of an attribute name, such as "epub:type".
    selectorText = selectorText.replace(RE_ATTRIB, (attrName) => attrName.replace(":", "\\3A "));
    return node.querySelector(selectorText);
  }
  catch(ex) {
    console.warn(`Failed to execute "querySelector" for selector "${selectorText}"`, ex, node);
    return null;
  }
}


/**
 * Adds CSS class @className to then HTML @element's class list if condition @isSet
 * is truthy, otherwise @className is removed from the @element's class list.
 *
 * @param {HTMLElement} element DOM node
 * @param {String} className CSS class name
 * @param {Boolean} isSet If truthy, @className is added to @element, otherwise removed
 */
function setClass(element, className, isSet) {
  if (!element || !element.classList || !className) {
    return;
  }
  if (isSet) {
    element.classList.add(className);
  }
  else {
    element.classList.remove(className);
  }
}


/**
 * Returns a promise that is resolved after @elem's transition on @transitionProperty
 * has ended _or_ the @timeout [ms] is reached.
 * The promise incl. @timeout is refreshed if there's a `transitionstart` event while
 * waiting for the `transitionend` event.
 *
 * @param {HTMLElement} elem DOM element with a @transitionProperty defined
 * @param {String} transitionProperty The transition property to wait for
 * @param {Number} [timeout=500] The max time to wait for a single transition event
 * @return {Promise} Promise resolved with the `transitionend` event or undefined if there's a timeout
 */
function waitUntilTransitionEnd(elem, transitionProperty, timeout = 500) {
  if (!elem) {
    return Promise.resolve();
  }
  return new Promise(function(resolve) {
    let numPendingTransitions = 0;
    let timerID = setTimeout(resolvePromise, timeout);

    function cancelTimeout() {
      if (timerID) {
        clearTimeout(timerID);
      }
    }

    function refreshTimeout() {
      cancelTimeout();
      timerID = setTimeout(resolvePromise, timeout);
    }

    function onTransitionStart(e) {
      if (e.target === elem && e.propertyName === transitionProperty) {
        numPendingTransitions += 1;
        refreshTimeout();
      }
    }

    function onTransitionEnd(e) {
      if (e.target === elem && e.propertyName === transitionProperty && --numPendingTransitions <= 0) {
        cancelTimeout();
        resolvePromise(e);
      }
    }

    function resolvePromise(e) {
      if (elem && elem.removeEventListener) {
        elem.removeEventListener("transitionstart", onTransitionStart);
        elem.removeEventListener("transitionend", onTransitionEnd);
      }
      resolve(e);
    }

    elem.addEventListener("transitionstart", onTransitionStart);
    elem.addEventListener("transitionend", onTransitionEnd);
  });
}


/**
 * Assigns attributes to elements found by the selector.
 * @param {Object} attributes - Object with keys/attributes pairs.
 * @param {string} selector - A CSS selector to find elements.
 */

function setAttributes(attributes, selector) {
  const elements = document.querySelectorAll(selector);

  // Iterate over each element and set the attributes
  elements.forEach(element => {
    for (const [key, value] of Object.entries(attributes)) {
      element.setAttribute(key, value);
    }
  });
}

function getVisibleTextInRange(contentDocument, start, end) {
  const startNode = contentDocument.getElementById(start.id);
  const endNode = contentDocument.getElementById(end.id);


  if (!startNode || !endNode) {
    return '';
  }

  function getTextBetweenNodes() {
    if(start.id===end.id){
      return startNode?.textContent?.substring(start.offset, end.offset)?.trim() || '';
    }
    let currentNode = startNode;
    let text = startNode?.textContent?.substring(start.offset)?.trim() + ' ' || '';
    let currentId = start.id;
    let splittedId = currentId.split('.');
    let firstNumber = Number(splittedId[1]);
    let secondNumber = Number(splittedId[2]);
    // if first node is an image it must check the node, not the text
    if (currentNode && (currentNode.querySelector('img') || currentNode.tagName.toLowerCase() === 'img')) {
      return '';
    }
    while (currentNode !== endNode) {
      if (currentNode && currentNode !== startNode)
        text += currentNode.textContent.trim() + ' ';
      firstNumber = secondNumber === 9 ? firstNumber + 1 : firstNumber;
      splittedId[1]=firstNumber;
      secondNumber = secondNumber === 9 ? 1 : secondNumber+1;
      splittedId[2]=secondNumber;
      currentId = `${splittedId.join('.')}`;
      currentNode = contentDocument.getElementById(currentId);
    }
    text += endNode.textContent?.substring(0, end.offset)?.trim();
    return text;
  }

  return getTextBetweenNodes();
}


/**
 * // BUG: Chrome in iOS
 * Chrome in iOS has a bug where the viewport height is not calculated correctly
 * when the app opens in a new window. This is a workaround to set the viewport height
 * forcing a recalculation of the layout. This does not happen in other browsers
 * or in a refresh.
 * This is a temporary solution until the bug is fixed in Chrome.
 *
 * @param {object} opts
 * @param {Element} opts.splashSelector
 * @param {string} opts.unitName
 * @param {number} opts.debounceMs
 */
function initViewportHeightFix(opts = {}) {
  const {
    splashScreenSelector = null,
    debounceMs      = 100,
    unitName        = '--vh'
  } = opts;


  /**
   * Detect if device is a pointer device (e.g. desktop or laptop)
   *
   * @returns { boolean }
   */
  function isPointerDevice() {
    return typeof window.matchMedia === 'function' &&
           window.matchMedia('(pointer: fine)').matches;
  }

  /**
   * Desktop / PC devices do not need viewport height fix
   */
  if (isPointerDevice()) {
    revealSplashScreen();
    return () => {};
  }


  let timeoutId;

  /**
   * Uses the visualViewport API on iOS 15+ or innerHeight to a CSS variable
   */
  function setViewportHeightVariable() {
    const vh = (window.visualViewport?.height || window.innerHeight) * 0.01;
    document.documentElement.style.setProperty(unitName, `${vh}px`);
  }

  /**
   * Debounced resize handler to set the viewport height variable
   */
  function onResize() {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(setViewportHeightVariable, debounceMs)
  }


  /**
   * In order to avoid a jumpiness of the splash screen logo,
   * it is revealed only after the viewport height variable is set.
   */
  function revealSplashScreen() {
    const splashScreen = document.querySelector(splashScreenSelector);

    if(!splashScreen) {
      return;
    }

    splashScreen.style.setProperty('opacity', 1);
  }

  /**
   * Initializes the viewport height fix by setting the viewport height variable.
   * It also sets up event listeners for orientation change and resize events.
   */
  function init() {
    setViewportHeightVariable();
    revealSplashScreen();

    window.addEventListener('orientationchange', onResize);
    window.addEventListener('resize', onResize);
  }

  if(document.readyState === 'loading') {
    document.addEventListener("DOMContentLoaded", init, { once: true })
    return;
  }

  init();

  /**
   * Cleans up the event listeners and timeout when the viewport height fix is no longer needed.
   */
  return () => {
    window.removeEventListener("resize", onResize);
    window.removeEventListener("orientationchange", onResize)
    clearTimeout(timeoutId)
  }
}



// -----------------------------------------------------------------------------
// EXPORTS
// -----------------------------------------------------------------------------

export {
  calcOffset,
  checkParserError,
  createDOMElement,
  getBoundingBox,
  getCascadedStyle,
  getComputedStyle,
  getComputedStyleValue,
  getMatchedCSSRules,
  getRange,
  getSelectedRange,
  getTextBoundingBox,
  getVisibleTextInRange,
  hasSVGSupport,
  initViewportHeightFix,
  isInlineElement,
  isValidClassToken,
  querySelector,
  setAttributes,
  setClass,
  waitUntilTransitionEnd
};
